/*
 * Shared helpers for cloud-management job state.
 *
 * `CM_ActLog.status` is free text written by several services, so the same
 * conceptual state arrives under a handful of spellings. Read it through these
 * predicates rather than comparing strings at call sites — the vocabulary is
 * documented in jarvis-api-mr/constants/cloudManagement.js.
 */

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/** What a cloud-management action was aimed at. Mirrors JOB_SCOPE in api-mr. */
export const JOB_SCOPE: Record<string, string> = {
  NETWORK: 'network',
  GATEWAY: 'gateway',
  TUNNEL: 'tunnel',
  UNKNOWN: 'unknown'
};

interface JobScopeRow {
  scope?: string;
  batchId?: string;
  entities?: {
    tunnelId?: string;
    instancePlanTypeBefore?: string;
  }[];
}

/**
 * Can this job be re-issued through the retry endpoint?
 *
 * Only network-wide actions can. A gateway- or tunnel-scoped retry loses the id
 * that narrowed it, and for a tunnel that is an escalation rather than a failure:
 * the re-issued action would target the whole network. The API refuses those with
 * a 400, so the button is hidden rather than offered and then rejected.
 *
 * Prefers the `scope` the API now returns. The entities fallback is for rows
 * written before that field existed and mirrors getJobScope in
 * jarvis-api-mr/constants/cloudManagement.js — keep the two in step. Anything
 * ambiguous is treated as not retryable, which matches the API refusing it.
 */
export const isRetryableScope = (row?: JobScopeRow): boolean => {
  if (!row) return false;

  if (row.scope) return row.scope === JOB_SCOPE.NETWORK;

  if (row.batchId) return true;

  const entities = Array.isArray(row.entities) ? row.entities : [];

  if (entities.length === 0) return false;
  if (entities.some(entity => entity?.tunnelId)) return false;
  if (entities.some(entity => entity?.instancePlanTypeBefore !== undefined)) {
    return false;
  }

  return entities.length > 1;
};

/** Statuses meaning the action is over, whatever the outcome. */
const TERMINAL_STATUSES = [
  'success',
  'failed',
  'failed(no response)',
  'started (failed)',
  'finished (success)',
  'finished (failed)'
];

/** Statuses meaning the platform is still working on it. */
const IN_PROGRESS_STATUSES = [
  'init',
  'started',
  'started (success)',
  'pending',
  'inprogress',
  'delay'
];

export const isTerminalCMStatus = (status?: string): boolean =>
  typeof status === 'string' &&
  TERMINAL_STATUSES.includes(status.trim().toLowerCase());

export const isInProgressCMStatus = (status?: string): boolean =>
  typeof status === 'string' &&
  IN_PROGRESS_STATUSES.includes(status.trim().toLowerCase());

/**
 * A finished action that did not succeed — including `failed(no response)`,
 * which is what the backstop stamps on a job it gave up waiting for. Those rows
 * are the ones an operator most wants to re-issue, so they must keep their
 * affordances after the job stops being "in progress".
 */
export const isFailedCMStatus = (status?: string): boolean =>
  isTerminalCMStatus(status) &&
  (status as string).trim().toLowerCase().includes('failed');

/**
 * Compact elapsed time, e.g. "45s", "12m", "2h 10m", "3d 4h".
 *
 * Kept coarse on purpose: the point is to make "this has been running for 21
 * hours" impossible to miss, not to report to the second.
 */
export const formatElapsed = (elapsedMs?: number | null): string => {
  if (typeof elapsedMs !== 'number' || !Number.isFinite(elapsedMs)) return '';

  const ms = Math.max(elapsedMs, 0);

  if (ms < MINUTE_MS) return `${Math.floor(ms / 1000)}s`;
  if (ms < HOUR_MS) return `${Math.floor(ms / MINUTE_MS)}m`;

  if (ms < DAY_MS) {
    const hours = Math.floor(ms / HOUR_MS);
    const minutes = Math.floor((ms % HOUR_MS) / MINUTE_MS);
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  }

  const days = Math.floor(ms / DAY_MS);
  const hours = Math.floor((ms % DAY_MS) / HOUR_MS);
  return hours ? `${days}d ${hours}h` : `${days}d`;
};

/** Milliseconds since `startedAt`, or null when it is missing or unparseable. */
export const getElapsedMs = (
  startedAt?: string | number | Date | null
): number | null => {
  if (!startedAt) return null;

  const started = new Date(startedAt).getTime();
  if (!Number.isFinite(started)) return null;

  return Date.now() - started;
};

interface BlockedActionDetail {
  action?: string;
  elapsedMs?: number;
  networkBeaconId?: string;
  networkBeaconStatus?: string;
}

/**
 * Turn a failed cloud-management request into something a human can act on.
 *
 * A 429 here is not rate limiting: it means another action already holds this
 * network. The backend now returns which action and how long it has been
 * running, so say that instead of the bare "Too Many Requests!" that made
 * P81-143028 look like a throttling problem to support.
 */
export const getCloudActionErrorMessage = (err: any): string => {
  const response = err?.response;
  const fallback = 'Something went wrong';

  if (!response) return fallback;

  const data = response.data;

  if (response.status === 429) {
    const detail: BlockedActionDetail = data?.body || {};
    const action = detail.action ? `A ${detail.action}` : 'Another action';
    const elapsed = formatElapsed(detail.elapsedMs);
    const since = elapsed ? ` (started ${elapsed} ago)` : '';

    return (
      `${action} is already running on this network${since}. ` +
      'Wait for it to finish, or release it from Cloud Management Logs.'
    );
  }

  return data?.message || fallback;
};
